import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PuzzlePieceIcon, 
  RectangleStackIcon, 
  SparklesIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import CreateMatchModal from '../components/CreateMatchModal';
import UpgradeOptionsModal from '../components/UpgradeOptionsModal';
import FileUploadModal from '../components/FileUploadModal';
import LoadingSpinner from '../components/LoadingSpinner';
import ModuleIntroModal from '../components/ModuleIntroModal';
import { t } from '../utils/translations';
import toast from 'react-hot-toast';
import matchService, { MatchGameListItem } from '../services/matchService';

const Match: React.FC = () => {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { subscription, checkLimit } = useSubscription();
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [matchGames, setMatchGames] = useState<MatchGameListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingGameId, setDeletingGameId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'deck-based' | 'custom' | 'ai-generated' | null>(null);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [editingMatch, setEditingMatch] = useState<MatchGameListItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);

  // Check if we should show the intro modal on first visit
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('matches_intro_seen');
    if (!hasSeenIntro) {
      setShowIntroModal(true);
    }
  }, []);

  const handleCloseIntroModal = () => {
    localStorage.setItem('matches_intro_seen', 'true');
    setShowIntroModal(false);
  };

  const fetchMatchGames = async () => {
    try {
      setLoading(true);
      const fetchedGames = await matchService.getMatchGames();
      setMatchGames(fetchedGames);
    } catch (error) {
      console.error('Error fetching match games:', error);
      toast.error('Erro ao carregar jogos de Match');
      setMatchGames([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchMatchGames();
    }
  }, [authLoading]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayGame = (gameId: string) => {
    // Navigate to match playing page
    navigate(`/matches/${gameId}/play`);
  };

  const handleCreateMatch = (type: 'deck-based' | 'custom' | 'ai-generated') => {
    setCreateType(type);
  };

  const handleFileUpload = () => {
    setShowFileUploadModal(true);
    setShowCreateModal(false);
  };

  const handleSearchMatches = () => {
    setShowCreateModal(false);
    setCreateType(null);
    navigate('/search?type=matches');
  };

  const handleFileUploadSubmit = async (formData: FormData) => {
    try {
      const result = await matchService.createMatchFromFile(formData);
      toast.success('Jogo de Match criado com sucesso a partir do arquivo!');
      setShowFileUploadModal(false);
      await fetchMatchGames();
      return result;
    } catch (error: any) {
      console.error('Error creating match from file:', error);
      throw error;
    }
  };

  const handleDeleteGame = async (gameId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este jogo de Match?')) {
      return;
    }

    try {
      setDeletingGameId(gameId);
      await matchService.deleteMatch(gameId);
      setMatchGames(matchGames.filter(g => g._id !== gameId));
      toast.success('Jogo de Match excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting match game:', error);
      toast.error('Erro ao excluir jogo de Match');
    } finally {
      setDeletingGameId(null);
    }
  };

  const handleEditMatch = (match: MatchGameListItem) => {
    setEditingMatch(match);
    setEditTitle(match.title);
    setEditDescription(match.description || '');
  };

  const handleCloseEditModal = () => {
    setEditingMatch(null);
    setEditTitle('');
    setEditDescription('');
  };

  const handleSaveEdit = async () => {
    if (!editingMatch) return;

    if (!editTitle.trim()) {
      toast.error('O título não pode estar vazio');
      return;
    }

    try {
      setSaving(true);
      await matchService.updateMatch(editingMatch._id, {
        title: editTitle,
        description: editDescription,
      });
      
      toast.success('Match atualizado com sucesso!');
      handleCloseEditModal();
      fetchMatchGames();
    } catch (error) {
      console.error('Error updating match:', error);
      toast.error('Erro ao atualizar match');
    } finally {
      setSaving(false);
    }
  };

  const filteredGames = matchGames.filter(game =>
    game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50/30 to-blue-50 dark:from-gray-900 dark:via-pink-900/10 dark:to-purple-900/20 transition-all duration-500">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 dark:from-pink-400 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent">
              Match
            </h1> 
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
              Crie e jogue jogos de correspondência para memorizar conceitos
            </p>
            {subscription && subscription.subscriptionTier === 'free' && !isAndroidWebView && (
              <div className="flex items-center space-x-2 text-sm">
                <span className={`font-medium ${matchGames.length >= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {matchGames.length}/3 jogos no plano gratuito
                </span>
                <Link 
                  to="/pricing"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold underline transition-colors duration-200"
                >
                  Upgrade
                </Link>
              </div>
            )}
          </div>
          <div className="mt-6 sm:mt-0">
            <button
              onClick={() => {
                // For now, use quiz limit until match is added to subscription limits
                if (!checkLimit('quiz', matchGames.length)) {
                  if (isAndroidWebView) {
                    // Show toast notification instead of modal
                    toast.error('Faça upgrade para criar mais do que 3 matches.', {
                      duration: 4000,
                      position: 'top-center',
                      style: {
                        background: '#EF4444',
                        color: '#fff',
                        fontWeight: 'bold',
                      },
                    });
                    return;
                  } else {
                    setShowUpgradePrompt(true);
                    return;
                  }
                }
                setShowCreateModal(true);
              }}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-pink-500 via-purple-600 to-blue-600 hover:from-pink-600 hover:via-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-pink-500/25 hover:shadow-pink-600/30"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar Match
            </button>
          </div>
        </div>

        {/* Search - Only show if there are at least 3 games */}
        {matchGames.length >= 3 && (
          <div className="relative mt-8 mb-6">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-200/50 dark:border-gray-700/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
            />
          </div>
        )}

        {/* Create Modal */}
        <CreateMatchModal
          isOpen={showCreateModal && createType !== null}
          onClose={() => {
            setShowCreateModal(false);
            setCreateType(null);
          }}
          type={createType}
          onMatchCreated={fetchMatchGames}
          onUpgradePrompt={() => setShowUpgradePrompt(true)}
        />

        {/* Type Selection Modal */}
        {showCreateModal && createType === null && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateType(null);
                }}
              />

              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-3 pt-4 pb-3 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 pr-2 leading-tight">
                    Como deseja criar seu match?
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateType(null);
                    }}
                    className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                    Escolha como gostaria de criar seu novo jogo de Match:
                  </p>

                  <button
                    onClick={() => handleCreateMatch('deck-based')}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <RectangleStackIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                          Do Deck
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Criar a partir de cards existentes
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleCreateMatch('ai-generated')}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                          Com IA
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Gerar automaticamente com IA
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={handleFileUpload}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-green-300 dark:border-green-600 rounded-lg hover:border-green-500 dark:hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <DocumentArrowUpIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-700 dark:group-hover:text-green-300">
                          Upload de Arquivo
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará pares automaticamente
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={handleSearchMatches}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-orange-300 dark:border-orange-600 rounded-lg hover:border-orange-500 dark:hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <MagnifyingGlassIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300">
                          Explorar Matches
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Copie matches compartilhados pela comunidade
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleCreateMatch('custom')}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-pink-300 dark:border-pink-600 rounded-lg hover:border-pink-500 dark:hover:border-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <PencilIcon className="h-6 w-6 sm:h-8 sm:w-8 text-pink-600 dark:text-pink-400 group-hover:text-pink-700 dark:group-hover:text-pink-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-pink-700 dark:group-hover:text-pink-300">
                          Do Zero
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Criar manualmente seus pares
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Match Games Grid */}
        {filteredGames.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-lg border border-gray-200 dark:border-gray-700">
            <PuzzlePieceIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {searchTerm ? 'Nenhum jogo encontrado' : 'Você ainda não tem jogos de Match'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? 'Tente ajustar os termos de busca' 
                : 'Comece criando seu primeiro jogo de Match'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-pink-500 via-purple-600 to-blue-600 hover:from-pink-600 hover:via-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transform hover:scale-105 transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Criar Primeiro Match
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 mt-8">
            {filteredGames.map((game) => (
              <div
                key={game._id}
                className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-xl rounded-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:border-pink-300/50 dark:hover:border-pink-500/50"
              >
                {/* Card Header */}
                <div className="relative bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 dark:from-gray-800 dark:via-pink-900/20 dark:to-purple-900/20 p-6 border-b border-gray-100/50 dark:border-gray-700/50">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-pink-500/5 to-purple-500/10"></div>
                  <div className="relative flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        game.type === 'deck-based' 
                          ? 'bg-blue-100 dark:bg-blue-900/30' 
                          : game.type === 'ai-generated'
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : 'bg-pink-100 dark:bg-pink-900/30'
                      }`}>
                        {game.type === 'deck-based' ? (
                          <RectangleStackIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        ) : game.type === 'ai-generated' ? (
                          <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <PencilIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                        )}
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        game.type === 'deck-based'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : game.type === 'ai-generated'
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          : 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                      }`}>
                        {game.type === 'deck-based' ? 'Do Deck' : game.type === 'ai-generated' ? 'IA' : 'Personalizado'}
                      </span>
                    </div>
                  </div>

                  <h3 className="relative text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
                    {game.title}
                  </h3>
                  {game.description && (
                    <p className="relative text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {game.description}
                    </p>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <PuzzlePieceIcon className="h-4 w-4 mr-2 text-pink-500" />
                      <span>{game.pairsCount} pares</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <PlayIcon className="h-4 w-4 mr-2 text-purple-500" />
                      <span>{game.attemptsCount} tentativas</span>
                    </div>
                  </div>

                  {/* Performance */}
                  {game.attemptsCount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Melhor tempo:</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        {formatTime(game.bestTime)}
                      </span>
                    </div>
                  )}

                  {/* Tags */}
                  {game.tags && game.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {game.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handlePlayGame(game._id)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transform hover:scale-105 transition-all duration-200"
                    >
                      <PlayIcon className="h-5 w-5 mr-2" />
                      Jogar
                    </button>
                    <button
                      onClick={() => navigate(`/matches/management/${game._id}`)}
                      className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transform hover:scale-105 transition-all duration-200"
                    >
                      <Cog6ToothIcon className="h-5 w-5 mr-2" />
                      Gerenciar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Match Modal */}
      {editingMatch && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Editar Match
              </h3>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all"
                  placeholder="Título do match"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all resize-none"
                  placeholder="Descrição do match (opcional)"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseEditModal}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={saving || !editTitle.trim()}
              >
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      <FileUploadModal
        isOpen={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        type="match"
        onSubmit={handleFileUploadSubmit}
        subscription={subscription}
      />

      {/* Upgrade Options Modal */}
      {showUpgradePrompt && (
        <UpgradeOptionsModal
          message="Você atingiu o limite de 3 matches no plano gratuito."
          feature="matches ilimitados"
          onClose={() => setShowUpgradePrompt(false)}
        />
      )}

      {/* Module Intro Modal */}
      <ModuleIntroModal
        isOpen={showIntroModal}
        onClose={handleCloseIntroModal}
        moduleType="matches"
      />
    </div>
  );
};

export default Match;